import { PersonHoverInfo } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/PersonHoverInfo';
import { UnassignedRoleBadge } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/UnassignedRoleBadge';
import { InvestmentProgrammeDocumentRes } from '@dealstack/shared/lib/generated';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { ActionIcon, Flex, Text } from '@mantine/core';
import { groupBy } from 'lodash';
import { useMemo, useState } from 'react';
import { BadgeContainer } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/BadgeContainer';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';

const MAX_VISIBLE_COUNTERPARTIES = 5;
const MAX_VISIBLE_COUNTERPARTIES_PER_ROLE = 3;

interface Props {
	counterparties: InvestmentProgrammeDocumentRes['counterparties'];
}

export const CounterpartiesDropdown = ({ counterparties: initialCounterparties }: Props) => {
	const [isOpen, setIsOpen] = useState(false);
	const tMipManage = useTranslationMipManage();

	const isPartiallyAssigned = useMemo(() => {
		const hasAssigned = initialCounterparties.some((i) => i.assigned);
		const hasUnassigned = initialCounterparties.some((i) => !i.assigned);
		return hasAssigned && hasUnassigned;
	}, [initialCounterparties]);

	const [visibleCounterparties, hiddenCounterparties] = useMemo(() => {
		const sorted = [...initialCounterparties].sort((a, b) => {
			if (a.assigned === b.assigned) {
				return 0;
			}
			return a.assigned ? -1 : 1;
		});
		if (sorted.length > MAX_VISIBLE_COUNTERPARTIES) {
			return [
				sorted.slice(0, MAX_VISIBLE_COUNTERPARTIES),
				sorted.slice(MAX_VISIBLE_COUNTERPARTIES),
			];
		}
		return [sorted, []];
	}, [initialCounterparties]);

	const counterpartiesPerRole = useMemo(() => {
		const counterpartiesPerRole = groupBy(initialCounterparties, 'roleName');
		return Object.entries(counterpartiesPerRole).map(([role, visibleCounterparties]) => {
			if (visibleCounterparties.length > MAX_VISIBLE_COUNTERPARTIES_PER_ROLE) {
				return {
					role,
					visibleCounterparties: visibleCounterparties.slice(
						0,
						MAX_VISIBLE_COUNTERPARTIES_PER_ROLE
					),
					hiddenCounterparties: visibleCounterparties.slice(
						MAX_VISIBLE_COUNTERPARTIES_PER_ROLE
					),
				};
			}
			return { role, visibleCounterparties, hiddenCounterparties: [] };
		});
	}, [initialCounterparties]);

	return (
		<Flex gap="xs" align="center">
			<ActionIcon
				size="xs"
				onClick={(e) => {
					e.stopPropagation();
					setIsOpen(!isOpen);
				}}
			>
				<Icon
					size="lg"
					variant={isOpen ? 'caret-down' : 'caret-right'}
					color={defaultTheme.v3.colors.black[0]}
				/>
			</ActionIcon>
			{!isOpen && (
				<Flex gap="0.25rem" wrap="wrap">
					{!isPartiallyAssigned && initialCounterparties.every((i) => !i.assigned) ? (
						<Text c={defaultTheme.v3.colors.gray[8]}>
							{tMipManage('documents_page.columns.counterparty.empty_roles')}
						</Text>
					) : (
						<>
							{visibleCounterparties.map((i, index) =>
								i.assigned ? (
									<PersonHoverInfo
										key={`${i.roleName ?? ''}-${i.firstName ?? ''}-${i.lastName ?? ''}-${index}`}
										person={i}
									/>
								) : (
									<UnassignedRoleBadge
										key={`${i.roleName ?? ''}-unassigned-${index}`}
										roleName={i.roleName}
									/>
								)
							)}
							{hiddenCounterparties?.length > 0 && (
								<BadgeContainer align="center" justify="center">
									<Text>+ {hiddenCounterparties.length}</Text>
								</BadgeContainer>
							)}
						</>
					)}
				</Flex>
			)}
			{isOpen && (
				<Flex direction="column" gap="0.25rem">
					{counterpartiesPerRole.map(
						({ role, visibleCounterparties, hiddenCounterparties }) => (
							<Flex key={role} align="center" gap="0.25rem" wrap="wrap">
								<Text>{role}</Text>
								<Flex gap="0.25rem" wrap="wrap">
									{visibleCounterparties.map((counterparty, index) =>
										counterparty.assigned ? (
											<PersonHoverInfo
												key={`${counterparty.roleName ?? ''}-${counterparty.firstName ?? ''}-${index}`}
												person={counterparty}
											/>
										) : (
											<UnassignedRoleBadge
												key={`${counterparty.roleName ?? ''}-unassigned-${index}`}
												roleName={counterparty.roleName}
											/>
										)
									)}
									{hiddenCounterparties?.length > 0 && (
										<BadgeContainer align="center" justify="center">
											<Text>+ {hiddenCounterparties.length}</Text>
										</BadgeContainer>
									)}
								</Flex>
							</Flex>
						)
					)}
				</Flex>
			)}
		</Flex>
	);
};
